import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  IStudentAssessmentListApiResponse,
  IStudentSubjectBasedAssessment,
  IStudentSubjectAssessment,
} from '../../../../apis/students/studentsAssessment.interfaces';
import { IScoreRecord } from '../../../../apis/score/score.interfaces';
import { useStudentSubjectBasedAssessmentListHook } from '../hooks/useStudentSubjectBasedAssessmentListHook';
import { useStudentSubjectBasedStudentAssessmentListHook } from '../hooks/useStudentSubjectBasedStudentAssessmentListHook';
import { useScoreListHook } from '../hooks/useScoreListHook';
import {
  useAddStudentAssessmentHook,
  usePatchStudentAssessmentHook,
} from '../hooks/useStudentAssessmentMutationsHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';

type TStudentAssessmentAppState = {
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  selectedAssessmentId?: string;
  isModalOpen: boolean;
  selectedAssessmentForEdit: IStudentSubjectAssessment | null;
  mutationErrorMessage?: string;
  isMutationLoading: boolean;
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  SET_SELECTED_ASSESSMENT_ID = 'SET_SELECTED_ASSESSMENT_ID',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_MODAL_OPEN = 'SET_IS_MODAL_OPEN',
  SET_SELECTED_ASSESSMENT_FOR_EDIT = 'SET_SELECTED_ASSESSMENT_FOR_EDIT',
  SET_MUTATION_ERROR_MESSAGE = 'SET_MUTATION_ERROR_MESSAGE',
  SET_MUTATION_LOADING = 'SET_MUTATION_LOADING',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_ID; payload: string }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_IS_MODAL_OPEN; payload: boolean }
  | {
      type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_FOR_EDIT;
      payload: IStudentSubjectAssessment | null;
    }
  | {
      type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE;
      payload: string | undefined;
    }
  | { type: ACTION_TYPE.SET_MUTATION_LOADING; payload: boolean };

const initialStudentAssessmentState: TStudentAssessmentAppState = {
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
  selectedAssessmentId: undefined,
  isModalOpen: false,
  selectedAssessmentForEdit: null,
  mutationErrorMessage: undefined,
  isMutationLoading: false,
};

type TState = {
  studentAssessmentAppState: TStudentAssessmentAppState;
  subjectBasedAssessmentList: IStudentSubjectBasedAssessment[];
  scoreRecordList: IScoreRecord[];
  studentAssessmentDetails?: IStudentAssessmentListApiResponse['data'];
  addAssessmentMutation: ReturnType<typeof useAddStudentAssessmentHook>;
  patchAssessmentMutation: ReturnType<typeof usePatchStudentAssessmentHook>;
};

const StudentAssessmentContext = createContext<TState | undefined>(undefined);
const StudentAssessmentDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const StudentAssessmentReducer = (
  appState: TStudentAssessmentAppState,
  action: Action
): TStudentAssessmentAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...appState,
        userParams: { ...appState.userParams, ...action.payload },
      };
    case ACTION_TYPE.SET_SELECTED_ASSESSMENT_ID:
      return {
        ...appState,
        selectedAssessmentId: action.payload,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_IS_MODAL_OPEN:
      return { ...appState, isModalOpen: action.payload };
    case ACTION_TYPE.SET_SELECTED_ASSESSMENT_FOR_EDIT:
      return { ...appState, selectedAssessmentForEdit: action.payload };
    case ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE:
      return { ...appState, mutationErrorMessage: action.payload };
    case ACTION_TYPE.SET_MUTATION_LOADING:
      return { ...appState, isMutationLoading: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StudentAssessmentProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [studentAssessmentAppState, dispatch] = useReducer(
    StudentAssessmentReducer,
    initialStudentAssessmentState
  );

  const {
    data: subjectBasedAssessmentListResponse,
    isLoading: subjectBasedAssessmentListLoading,
    isError: subjectBasedAssessmentListError,
    isSuccess: subjectBasedAssessmentListSuccess,
    error: subjectBasedAssessmentListDataError,
  } = useStudentSubjectBasedAssessmentListHook(
    studentAssessmentAppState.userParams
  );

  const {
    data: scoreListResponse,
  } = useScoreListHook(studentAssessmentAppState.userParams);

  const {
    data: studentAssessmentDetailsResponse,
    isLoading: studentAssessmentDetailsLoading,
    isError: studentAssessmentDetailsError,
    isSuccess: studentAssessmentDetailsSuccess,
    error: studentAssessmentDetailsDataError,
  } = useStudentSubjectBasedStudentAssessmentListHook(
    studentAssessmentAppState.selectedAssessmentId || '',
    {
      enabled: !!studentAssessmentAppState.selectedAssessmentId,
    } as Parameters<typeof useStudentSubjectBasedStudentAssessmentListHook>[1]
  );

  const addAssessmentMutation = useAddStudentAssessmentHook({
    onSuccess: () => {
      dispatch({ type: ACTION_TYPE.SET_IS_MODAL_OPEN, payload: false });
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_FOR_EDIT,
        payload: null,
      });
      dispatch({
        type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
        payload: undefined,
      });
      dispatch({ type: ACTION_TYPE.SET_MUTATION_LOADING, payload: false });
    },
    onError: (error) => {
      dispatch({
        type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
        payload:
          error?.response?.data?.message ||
          ERROR_MESSAGE.GET_STUDENT_LIST_FAILURE,
      });
      dispatch({ type: ACTION_TYPE.SET_MUTATION_LOADING, payload: false });
    },
    onMutate: () => {
      dispatch({ type: ACTION_TYPE.SET_MUTATION_LOADING, payload: true });
      dispatch({
        type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
        payload: undefined,
      });
    },
  });

  const patchAssessmentMutation = usePatchStudentAssessmentHook({
    onSuccess: () => {
      dispatch({ type: ACTION_TYPE.SET_IS_MODAL_OPEN, payload: false });
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_FOR_EDIT,
        payload: null,
      });
      dispatch({
        type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
        payload: undefined,
      });
      dispatch({ type: ACTION_TYPE.SET_MUTATION_LOADING, payload: false });
    },
    onError: (error) => {
      dispatch({
        type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
        payload:
          error?.response?.data?.message ||
          ERROR_MESSAGE.GET_STUDENT_LIST_FAILURE,
      });
      dispatch({ type: ACTION_TYPE.SET_MUTATION_LOADING, payload: false });
    },
    onMutate: () => {
      dispatch({ type: ACTION_TYPE.SET_MUTATION_LOADING, payload: true });
      dispatch({
        type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
        payload: undefined,
      });
    },
  });

  const loadingPageData = useMemo(
    () => subjectBasedAssessmentListLoading || studentAssessmentDetailsLoading,
    [subjectBasedAssessmentListLoading, studentAssessmentDetailsLoading]
  );

  const loadingPageDataSuccess = useMemo(
    () => subjectBasedAssessmentListSuccess || studentAssessmentDetailsSuccess,
    [subjectBasedAssessmentListSuccess, studentAssessmentDetailsSuccess]
  );

  const loadingPageDataError = useMemo(
    () => subjectBasedAssessmentListError || studentAssessmentDetailsError,
    [subjectBasedAssessmentListError, studentAssessmentDetailsError]
  );

  useEffect(() => {
    onStudentAssessmentStateChange();
    // eslint-disable-next-line
  }, [
    subjectBasedAssessmentListDataError,
    studentAssessmentDetailsDataError,
    loadingPageDataError,
    subjectBasedAssessmentListLoading,
    studentAssessmentDetailsLoading,
    loadingPageData,
    loadingPageDataSuccess,
    subjectBasedAssessmentListError,
    studentAssessmentDetailsError,
  ]);

  const onStudentAssessmentStateChange = () => {
    if (loadingPageData) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (loadingPageDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (loadingPageDataError) {
      const errorMessage = subjectBasedAssessmentListError
        ? (subjectBasedAssessmentListDataError?.response?.data.message ??
          ERROR_MESSAGE.GET_STUDENT_LIST_FAILURE)
        : studentAssessmentDetailsError
          ? (studentAssessmentDetailsDataError?.response?.data.message ??
            ERROR_MESSAGE.GET_STUDENT_LIST_FAILURE)
          : '';
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: errorMessage,
      });
    }
  };

  const currentState = useMemo<TState>(
    () => ({
      studentAssessmentAppState,
      subjectBasedAssessmentList:
        subjectBasedAssessmentListResponse?.data ?? [],
      scoreRecordList: scoreListResponse?.data ?? [],
      studentAssessmentDetails: studentAssessmentDetailsResponse?.data,
      addAssessmentMutation,
      patchAssessmentMutation,
    }),
    [
      studentAssessmentAppState,
      subjectBasedAssessmentListResponse,
      scoreListResponse,
      studentAssessmentDetailsResponse,
      addAssessmentMutation,
      patchAssessmentMutation,
    ]
  );

  return (
    <StudentAssessmentContext.Provider value={currentState}>
      <StudentAssessmentDispatchContext.Provider value={dispatch}>
        {children}
      </StudentAssessmentDispatchContext.Provider>
    </StudentAssessmentContext.Provider>
  );
};

export const useStudentAssessmentState = () => {
  const state = useContext(StudentAssessmentContext);
  if (state === undefined) {
    throw new Error(
      'useStudentAssessmentState must be used within a StudentAssessmentProvider'
    );
  }
  return state;
};

export const useStudentAssessmentActions = () => {
  const state = useContext(StudentAssessmentContext);
  const dispatch = useContext(StudentAssessmentDispatchContext);
  if (!dispatch || !state) {
    throw new Error(
      'useStudentAssessmentActions must be used within a StudentAssessmentProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const setSelectedAssessmentId = (id: string) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_ID, payload: id });
  };

  const clearSelectedAssessment = () => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_ID, payload: '' });
  };

  const openModal = (assessment?: IStudentSubjectAssessment) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_FOR_EDIT,
      payload: assessment || null,
    });
    dispatch({ type: ACTION_TYPE.SET_IS_MODAL_OPEN, payload: true });
    dispatch({
      type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
      payload: undefined,
    });
  };

  const closeModal = () => {
    dispatch({ type: ACTION_TYPE.SET_IS_MODAL_OPEN, payload: false });
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_ASSESSMENT_FOR_EDIT,
      payload: null,
    });
    dispatch({
      type: ACTION_TYPE.SET_MUTATION_ERROR_MESSAGE,
      payload: undefined,
    });
  };

  const handleSubmitAssessment = (assessment: IStudentSubjectAssessment) => {
    if (assessment.id) {
      state.patchAssessmentMutation.mutate(assessment);
    } else {
      state.addAssessmentMutation.mutate(assessment);
    }
  };

  return {
    updateSearchParams,
    setSelectedAssessmentId,
    clearSelectedAssessment,
    openModal,
    closeModal,
    handleSubmitAssessment,
  };
};
